import { useId, useState } from 'react';
import styles from './crm.module.css';
import { CrmPopover } from './CrmPopover';
import type { CrmView } from '../../types';

interface Props {
  views: CrmView[];
  /** the view currently applied to the grid (marked "(loaded)"), or null */
  loadedViewId: number | null;
  onLoad: (v: CrmView) => void;
  /** ★ make this the CALLER's personal default */
  onSetDefault: (v: CrmView) => void;
  /** overwrite the saved config with the grid's current layout */
  onUpdateToCurrent: (v: CrmView) => void;
  /** flip shared. May be REFUSED by the server when the view is the team default. */
  onToggleShared: (v: CrmView) => void;
  /** rename — owner-only, so this is only ever called for `v.is_mine` rows */
  onRename: (v: CrmView, name: string) => void;
  onDelete: (v: CrmView) => void;
  /**
   * ADMIN-only: point the LIST's team default at this shared view, or clear it. `undefined` for a
   * non-admin, which is what hides the menu item — non-admins still see the marker (the team default
   * is not a secret), they just can't move it. Mirrors the server's `require_admin` route gate on
   * `PUT /api/crm/admin/lists/{id}/team-default-view`, which is the actual authority.
   */
  onSetTeamDefault?: (v: CrmView, next: boolean) => void;
}

/**
 * The Views panel of the grid's side panel.
 *
 * ## Layout (owner request 2026-08-31)
 *
 * Two sections — **SHARED** on top, **MY VIEWS** below — and every view has exactly ONE home. The
 * grouping key is `shared`, not ownership, because that is the split the data model actually
 * supports: `list_views` returns the caller's own views PLUS every shared view on the list, so a
 * private view is always `is_mine`, and a view the caller owns AND shares belongs under SHARED (one
 * home, the shared one). Grouping by `is_mine` instead would file an owner's shared view in a
 * section its readers cannot see — the same view sorted two different ways depending on who looks.
 *
 * ## The row
 *
 * ★ (set as my personal default) stays on the row, always visible. **Every other action lives in the
 * ⋯ menu**, whose trigger fades in on row hover — `opacity`, never `display:none`/`visibility`, so it
 * stays in the tab order and a keyboard user reaches it (`:focus-visible` brings it back to full
 * opacity). The menu itself is the shared `CrmPopover` primitive (portaled, Escape/outside-click
 * close, arrow-key item cycling).
 *
 * ⚠ **★ is owner-only, and that is a backend fact rather than a UI choice.** `is_default` is a column
 * on the view ROW scoped per (list, owner), and `PATCH /api/crm/views/{id}` is owner-only (a
 * non-owner gets an existence-safe 404) — so a star on somebody else's shared view would be a control
 * that can only fail. A member who wants a shared view to open by default is served by the admin-set
 * TEAM default, which is exactly what that mechanism is for. The slot is held open so rows stay
 * aligned.
 *
 * Extracted from `CrmListGrid` so it can actually be tested: the grid itself cannot render in jsdom
 * (~24 useCrm hooks plus a live AgGridReact), which is the same reason `crmRecordSelection` and
 * `CrmColumnsPanel` are leaf modules. Purely presentational — every mutation, and the delete
 * confirmation, stays with the parent.
 *
 * Two DIFFERENT defaults meet on this row, and the wording matters: `★ / is_default` is the CALLER's
 * OWN default; `◆ / team default` is the list's, set by an admin and applied on open to anyone with
 * no personal default of their own.
 */
export function CrmViewsPanel({
  views, loadedViewId, onLoad, onSetDefault, onUpdateToCurrent, onToggleShared, onRename, onDelete,
  onSetTeamDefault,
}: Props) {
  // the view whose name is being edited inline, plus the draft text. Transient UI — never URL state.
  const [renaming, setRenaming] = useState<{ id: number; text: string } | null>(null);
  // the row whose ⋯ menu is open, as {id, anchor} — one menu at a time.
  const [menu, setMenu] = useState<{ id: number; anchor: HTMLElement } | null>(null);
  // stable per-instance prefix for the section headings the groups below point at
  const headingId = useId();

  const commitRename = (v: CrmView) => {
    const next = renaming?.text.trim() ?? '';
    setRenaming(null);
    // empty → no-op, keeping the old name (mirrors update_view's own semantics; the server also
    // truncates to 120 chars). Unchanged → no round-trip.
    if (!next || next === v.name) return;
    onRename(v, next);
  };

  const closeMenu = () => setMenu(null);

  const renderRow = (v: CrmView) => {
    // an admin's team-default item is the only action a non-owner ever gets, so a row with neither
    // renders no trigger at all rather than an empty menu.
    const canTeamDefault = !!onSetTeamDefault && v.shared;
    const hasMenu = v.is_mine || canTeamDefault;
    // ONE phrasing for the star, used as both the tooltip and the accessible name — two near-identical
    // strings are two things to keep in sync, and a screen reader would read one of them out of context.
    const starLabel = v.is_default
      ? `${v.name} is your default view`
      : `Make ${v.name} your default view`;
    // The row's TEXT says nothing about sharing when the caller owns the view — the SHARED section
    // heading above it does, and repeating it on every row is what the owner's cleanup removed. But a
    // heading a screen reader passes over (and the row's own name, read alone by a rotor) leaves an
    // own-and-shared view indistinguishable from a private one, so the cue lives in the accessible
    // name instead. Words, not the ◆ glyph, which reads as punctuation.
    const loadLabel = [
      v.name,
      v.shared ? 'shared' : null,
      !v.is_mine ? 'team' : null,
      v.is_team_default ? 'team default' : null,
      loadedViewId === v.id ? 'loaded' : null,
    ].filter(Boolean).join(' · ');

    return (
      <div key={v.id} className={styles.viewRow}>
        {v.is_mine ? (
          <button
            className={styles.viewStar}
            title={starLabel}
            aria-label={starLabel}
            aria-pressed={v.is_default}
            disabled={v.is_default}
            onClick={() => onSetDefault(v)}
          >{v.is_default ? '★' : '☆'}</button>
        ) : (
          // keeps the label column aligned with the rows that DO have a star
          <span className={styles.viewStarSpacer} aria-hidden="true" />
        )}

        {renaming?.id === v.id ? (
          <input
            className={styles.gridViewRename}
            aria-label={`Rename view ${v.name}`}
            autoFocus
            value={renaming.text}
            onChange={(e) => setRenaming({ id: v.id, text: e.target.value })}
            onBlur={() => commitRename(v)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') commitRename(v);
              if (e.key === 'Escape') setRenaming(null);
            }}
          />
        ) : (
          <button className={styles.gridViewLoad} aria-label={loadLabel} onClick={() => onLoad(v)}>
            {v.is_team_default ? '◆ ' : ''}{v.name}
            {v.shared && !v.is_mine ? ' · (team)' : ''}
            {v.is_team_default ? ' · team default' : ''}
            {loadedViewId === v.id ? ' ·  (loaded)' : ''}
          </button>
        )}

        {hasMenu && (
          <button
            className={styles.viewKebab}
            aria-label={`${v.name} view actions`}
            aria-haspopup="menu"
            aria-expanded={menu?.id === v.id}
            onClick={(e) => setMenu(menu?.id === v.id ? null : { id: v.id, anchor: e.currentTarget })}
          >⋯</button>
        )}

        {menu?.id === v.id && (
          <CrmPopover anchorEl={menu.anchor} onClose={closeMenu} align="right">
            {v.is_mine && (
              <>
                <button role="menuitem" className={styles.popoverItem}
                        onClick={() => { closeMenu(); onUpdateToCurrent(v); }}>Update to current view</button>
                <button role="menuitem" className={styles.popoverItem}
                        onClick={() => { closeMenu(); setRenaming({ id: v.id, text: v.name }); }}>Rename view</button>
                <button role="menuitem" className={styles.popoverItem}
                        onClick={() => { closeMenu(); onToggleShared(v); }}>
                  {v.shared ? 'Make private' : 'Share with team'}
                </button>
              </>
            )}
            {canTeamDefault && (
              <button role="menuitem" className={styles.popoverItem}
                      onClick={() => { closeMenu(); onSetTeamDefault?.(v, !v.is_team_default); }}>
                {v.is_team_default ? 'Clear team default' : 'Set as team default'}
              </button>
            )}
            {v.is_mine && (
              <button role="menuitem" className={`${styles.popoverItem} ${styles.popoverDanger}`}
                      onClick={() => { closeMenu(); onDelete(v); }}>Delete view</button>
            )}
          </CrmPopover>
        )}
      </div>
    );
  };

  if (views.length === 0) return <div className={styles.gridEmpty}>No saved views yet.</div>;

  const shared = views.filter((v) => v.shared);
  const mine = views.filter((v) => !v.shared);

  // The two headings are styled divs, which carry no structure at all: to anything but a sighted
  // reader the panel was one undifferentiated run of rows. role="group" + aria-labelledby gives each
  // run the heading as its own accessible name without changing a pixel.
  return (
    <>
      {shared.length > 0 && (
        <div role="group" aria-labelledby={`${headingId}-shared`}>
          <div className={styles.colSection} id={`${headingId}-shared`}>Shared</div>
          {shared.map(renderRow)}
        </div>
      )}
      {mine.length > 0 && (
        <div role="group" aria-labelledby={`${headingId}-mine`}>
          <div className={styles.colSection} id={`${headingId}-mine`}>My views</div>
          {mine.map(renderRow)}
        </div>
      )}
    </>
  );
}
